import React from 'react'
import { graphql } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import { Sticky, StickyContainer } from 'react-sticky'

import { Layout } from '../components/Layout'
import { components } from '../slices'
import SliceZoneComponent from '../components/SliceZone'
import Aside from '../components/storyPage/Aside'
import StoryHead from '../components/storyPage/Head'
import Filters from '../components/storyPage/Filters'
import QuoteSectionComponent from '../components/QuoteSection'
import { SLICES } from '../constants/slices'
import { filterSlice, findSlice } from '../utils/getSlice'
import { getDefaultPageData } from '../utils/getDefaultPageData'
import { useMatchMedia } from '../hooks/useMatchMedia'
import { useHeaderDataContext } from '../hooks/useHeaderData'
import { SEO } from '../components/Head'

const StoryPageTemplate = ({ data }) => {
  if (!data) return null

  const headerArticlesData = data.headerArticles.nodes
  const contactFormData = data.prismicContactForm

  const { isMatching, calculated } = useMatchMedia('(max-width:992px)', true)
  const headerData = useHeaderDataContext()

  const { topLine, footer, header, page, activeDoc, topMenu } =
    getDefaultPageData(data, 'prismicStoryPage')

  const filtersData = page.filters.filter((filter, i) => i < 2)
  const asideData = page.filters.filter((filter, i) => i > 2)

  const formSlice = findSlice(page.body1, SLICES.storyContactFormSection)
  const slicesWithoutForm = filterSlice(
    page.body1,
    SLICES.storyContactFormSection,
  )
  const brand = page?.brand?.document?.data?.image
  const person = page?.person?.document?.data

  const renderAside = () => (
    <Aside
      data={asideData}
      logo={page?.brand}
      description={page?.description?.text}
      resultsLabel={page?.label1}
      results={page?.results}
      mobileFilters={page?.filters}
    />
  )

  return (
    <Layout
      topMenu={topMenu.data}
      footer={footer.data}
      topLine={topLine.data}
      header={{ nav: header.data.nav, articles: header.data.articles }}
      activeDocMeta={activeDoc}
      className="story-page"
      headerArticlesData={headerArticlesData}
    >
      <StoryHead
        videoPreview={page.video_preview.url}
        video={page.video_link?.url}
        title={page.title.text}
        label={page.label.text}
        brand={brand}
      />
      <Filters data={filtersData} />

      <div className="container">
        <section className="section">
          <div>
            <QuoteSectionComponent
              quote={person?.quote?.text}
              authorAvatar={person?.photo}
              authorName={person?.name}
              authorPosition={person?.position}
              backgroundImage={page?.section_image}
            />
            <SliceZoneComponent
              slices={slicesWithoutForm}
              components={components}
            />
          </div>
        </section>
        {!isMatching && calculated ? (
          <StickyContainer className="aside-wrapper">
            <Sticky
              disableCompensation
              topOffset={headerData?.visibleTopLine ? -150 : -110}
              bottomOffset={180}
            >
              {({ style, isSticky }) => (
                <div style={style} className={isSticky ? 'sticky' : ''}>
                  {renderAside()}
                </div>
              )}
            </Sticky>
          </StickyContainer>
        ) : (
          renderAside()
        )}
      </div>
      <SliceZoneComponent
        slices={[formSlice]}
        components={components}
        context={{ contactFormData }}
      />
    </Layout>
  )
}

export const Head = ({ data }) => {
  const { page, pageContent } = getDefaultPageData(data, 'prismicStoryPage')
  return (
    <SEO
      meta={{
        title: page?.meta_title,
        description: page?.meta_description,
        image: page?.meta_image,
        type: page?.type,
        pageContent,
      }}
    />
  )
}

export const query = graphql`
  query storyQuery($id: String, $lang: String) {
    prismicStoryPage(id: { eq: $id }, lang: { eq: $lang }) {
      _previewable
      url
      uid
      type
      id
      lang
      alternate_languages {
        id
        type
        lang
        uid
      }
      data {
        meta_title {
          text
        }
        meta_image {
          url
          alt
        }
        meta_description {
          text
        }
        body1 {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...StoryPageDataBody1HeaderBig
          ...StoryPageDataBody1HeaderMedium
          ...StoryPageDataBody1HeaderSmall
          ...StoryPageDataBody1Paragraph16Padding
          ...StoryPageDataBody1Quote
          ...StoryPageDataBody1Image
          ...StoryPageDataBody1List
          ...StoryPageDataBody1StoryContactFormSection
        }
        video_link {
          url
        }
        video_preview {
          alt
          url
        }
        title {
          text
        }
        label {
          text
        }
        brand {
          document {
            ... on PrismicBrand {
              id
              data {
                image {
                  gatsbyImageData
                  alt
                }
              }
            }
          }
        }
        filters {
          filters_description {
            text
          }
          filters_label {
            text
          }
        }
        description {
          text
        }
        person {
          document {
            ... on PrismicPerson {
              id
              data {
                name
                photo {
                  gatsbyImageData
                  alt
                }
                quote {
                  text
                }
                position
              }
            }
          }
        }
        section_image {
          gatsbyImageData
          alt
        }
        label1 {
          text
        }
        results {
          text {
            text
          }
          big_label {
            text
          }
        }
      }
    }
    prismicTopLine(lang: { eq: $lang }) {
      ...TopLineFragment
    }
    prismicHeader(lang: { eq: $lang }) {
      data {
        articles {
          article_link {
            document {
              ... on PrismicArticlePage {
                ...ArticlePage
              }
            }
          }
        }
        nav {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...HeaderDataNavFooter
          ...HeaderDataNavButton
          ...HeaderDataNavNavItem
        }
      }
    }
    prismicFooter(lang: { eq: $lang }) {
      data {
        body {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...FooterDataBodyCopyright
          ...FooterDataBodyFooter
          ...FooterDataBodySocials
          ...FooterDataBodyAwards
          ...FooterDataBodyForm
        }
        body1 {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...FooterDataBody1NavItem
        }
      }
    }
    headerArticles: allPrismicArticlePage(
      filter: {
        lang: { eq: $lang }
        data: { invisible: { eq: "no" }, type: { eq: "blog" } }
      }
      limit: 2
      sort: { first_publication_date: DESC }
    ) {
      nodes {
        ...ArticlePage
      }
    }
    prismicContactForm(lang: { eq: $lang }) {
      ...ContactForm
    }
  }
`

export default withPrismicPreview(StoryPageTemplate)
